import { Button, Link, PlaceholderImage, Typography, WroomImage } from '@components'
import { Search as SearchComponent } from '@haus-storefront-react/search'
import { useTranslation } from 'react-i18next'
import type { SearchConfigStyles } from './types'
import { SearchProductList } from './SearchProductList'
import styles from './Search.module.scss'

interface SearchTypedResultsProps {
  searchTerm: string
  configStyles?: SearchConfigStyles
}

export function SearchTypedResults({ searchTerm, configStyles }: SearchTypedResultsProps) {
  const { t } = useTranslation()

  return (
    <>
      <SearchComponent.Results>
        {({ isLoading, items, collections, error }) => {
          if (isLoading) {
            return (
              <div className={styles.loading} role="status" aria-live="polite" aria-busy="true">
                <Typography variant="body">{t('search.loading')}</Typography>
              </div>
            )
          }

          if (items?.length === 0)
            return (
              <div className={styles.empty} role="status">
                <Typography variant="body">{t('search.noResults')}</Typography>
              </div>
            )
          if (error) {
            return (
              <div className={styles.error} role="alert">
                <Typography variant="body">{t('search.error')}</Typography>
              </div>
            )
          }
          const hasResults = (collections?.length ?? 0) > 0 || (items?.length ?? 0) > 0
          if (!hasResults) return null

          const firstCollection = collections?.[0]

          return (
            <div className={styles.resultsWrapper}>
              {firstCollection ? (
                <SearchComponent.CollectionItem collection={firstCollection}>
                  <Link
                    href={
                      firstCollection.slug
                        ? t('urls.category', {
                            collectionSlug: firstCollection.slug,
                          })
                        : '#'
                    }
                    className={styles.collectionItem}
                    aria-label={firstCollection.name}
                  >
                    <div className={styles.collectionImageWrapper}>
                      {firstCollection.featuredAsset?.preview ? (
                        <WroomImage
                          src={firstCollection.featuredAsset.preview}
                          className={styles.collectionImage}
                          alt={firstCollection.name}
                          width={64}
                          ratio="1:1"
                        />
                      ) : (
                        <PlaceholderImage className={styles.collectionImage} />
                      )}
                    </div>
                    <span className={styles.collectionName}>
                      <Typography
                        variant={configStyles?.nameTypography?.variant ?? 'body'}
                        as={configStyles?.nameTypography?.tag ?? 'span'}
                        color={configStyles?.nameTypography?.color ?? 'body'}
                        style={{ textDecoration: 'inherit' }}
                      >
                        {firstCollection.name}
                      </Typography>
                    </span>
                  </Link>
                </SearchComponent.CollectionItem>
              ) : null}
              <SearchProductList configStyles={configStyles} items={items ?? []} />
              {searchTerm && (
                <Button
                  onClick={() => {
                    window.location.href = t('urls.search', {
                      term: encodeURIComponent(searchTerm),
                    })
                  }}
                  variant={configStyles?.showAllButtonVariant?.variant ?? 'primary'}
                  fullWidth
                  className={styles.viewFullResults}
                  aria-label={t('a11y.search.aria.showAllResultsFor', { searchTerm })}
                >
                  {t('searchField.showAllHits')}
                </Button>
              )}
            </div>
          )
        }}
      </SearchComponent.Results>
    </>
  )
}
